import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cancelSpotOrder,
  fetchMarketCatalog,
  fetchMarketPrices,
  fetchSpotBook,
  fetchSpotFx,
  fetchSpotMarkets,
  fetchSpotMeta,
  fetchSpotOpenOrders,
  fetchSpotOhlc,
  fetchSpotTicker,
  placeSpotOrder,
  type MarketCatalog,
  type MarketPriceItem,
  type OhlcCandle,
  type SpotBook,
  type SpotBookLevel,
  type SpotMarketRow,
  type SpotOpenOrder,
} from "@/api/market";
import { fetchDepositMe, fetchIrrBalance, fetchNmcBalances, listWallets } from "@/api/wallets";
import { errorMessage } from "@/api/auth";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";
import { ChipTab, SelectField, StatusChip } from "@/ui/wx";
import { OrderBook } from "@/modules/market/OrderBook";
import { TradeForm } from "@/modules/market/TradeForm";
import { MarketList } from "@/modules/market/MarketList";
import { ChartTabs, PairStats } from "@/modules/market/PairChart";
import { fmtAmount, fmtSpotPrice, isMetalBase, normalizeSpotPrice, SPOT_PAIR_HINT_KEY } from "@/lib/format";
import { getNetworkEnv } from "@/network/env";

type Mode = "prices" | "spot";

function levelsOf(book: SpotBook | null, side: "bids" | "asks"): SpotBookLevel[] {
  if (!book) return [];
  if (side === "bids") return book.bids || book.buy || book.levels?.bids || [];
  return book.asks || book.sell || book.levels?.asks || [];
}

function orderId(o: SpotOpenOrder): string {
  return String(o.orderId || o.id || o.public_id || "");
}

function baseOf(pair: string): string {
  const i = pair.indexOf("_");
  return i > 0 ? pair.slice(0, i) : pair;
}

function quoteOf(pair: string): string {
  const i = pair.indexOf("_");
  return i > 0 ? pair.slice(i + 1).toUpperCase() : "USDT";
}

function displayLast(raw: unknown, pair: string): string {
  return fmtSpotPrice(raw, pair);
}

function readPairHint(): string | null {
  try {
    const v = sessionStorage.getItem(SPOT_PAIR_HINT_KEY);
    return v ? v.toUpperCase() : null;
  } catch {
    return null;
  }
}

export function MarketPanel({
  initialMode = "spot",
  requireAuthForTrade = false,
  onNeedAuth,
}: {
  initialMode?: Mode;
  requireAuthForTrade?: boolean;
  onNeedAuth?: () => void;
}) {
  const { token } = useSession();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [spotNet, setSpotNet] = useState("all");
  const [catalog, setCatalog] = useState<MarketCatalog | null>(null);
  const [prices, setPrices] = useState<Record<string, MarketPriceItem>>({});
  const [warn, setWarn] = useState("");
  const [spotRows, setSpotRows] = useState<SpotMarketRow[]>([]);
  const [spotMeta, setSpotMeta] = useState<string[]>(["market", "limit"]);
  const [pair, setPair] = useState(() => readPairHint() || "NAG_USDT");
  const [book, setBook] = useState<SpotBook | null>(null);
  const [ticker, setTicker] = useState("—");
  const [fx, setFx] = useState("—");
  const [opens, setOpens] = useState<SpotOpenOrder[]>([]);
  const [side, setSide] = useState<"buy" | "sell">("buy");
  const [orderType, setOrderType] = useState("market");
  const [amountNag, setAmountNag] = useState("1");
  const [limit, setLimit] = useState("");
  const [trigger, setTrigger] = useState("");
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ordersTab, setOrdersTab] = useState<"open" | "stop">("open");
  const [chartTab, setChartTab] = useState<"nanom" | "global" | "depth">("nanom");
  const [ohlc, setOhlc] = useState<OhlcCandle[]>([]);
  const [ohlcSource, setOhlcSource] = useState("");
  const [ohlcEmpty, setOhlcEmpty] = useState<string | null>(null);
  const [tickerMeta, setTickerMeta] = useState<{
    high?: string;
    low?: string;
    volume?: string;
    index?: string;
  }>({});
  const [availQty, setAvailQty] = useState(0);
  const [availLabel, setAvailLabel] = useState("موجودی");
  const [availText, setAvailText] = useState("—");
  const [px, setPx] = useState(0);

  const filteredPairs = useMemo(() => {
    const rows =
      spotNet === "all"
        ? spotRows
        : spotRows.filter((r) => String(r.networkCode || "").toLowerCase() === spotNet);
    const pairs = rows
      .map((r) => String(r.pair || r.symbol || "").toUpperCase())
      .filter(Boolean);
    return [...new Set(pairs)];
  }, [spotRows, spotNet]);

  const activeRow = useMemo(
    () => spotRows.find((r) => String(r.pair || r.symbol || "").toUpperCase() === pair),
    [spotRows, pair]
  );

  const refreshPrices = useCallback(async () => {
    try {
      const items = await fetchMarketPrices();
      const map: Record<string, MarketPriceItem> = {};
      for (const it of items) map[`${it.networkCode}:${it.symbol}`] = it;
      setPrices(map);
    } catch {
      /* keep last */
    }
  }, []);

  const refreshSpot = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        fetchSpotBook(token || "", pair),
        fetchSpotTicker(token || "", pair).catch(() => ({})),
      ]);
      setBook(b);
      if ((b as { degraded?: boolean }).degraded) setWarn("عمق محدود");
      else setWarn("");
      let last = (t as { last?: unknown }).last ?? (t as { price?: unknown }).price;
      if (last == null || last === "") {
        const base = baseOf(pair).toUpperCase();
        const vals = Object.values(prices);
        const hit2 =
          vals.find((p) => String(p.symbol || "").toUpperCase() === base) ||
          prices[`nmc:${base}`] ||
          prices[`bsc:${base}`];
      if (hit2?.priceUsd != null) last = hit2.priceUsd;
      }
      setTicker(last != null && last !== "" ? displayLast(last, pair) : "—");
      const high = (t as { high?: unknown; high24h?: unknown }).high ?? (t as { high24h?: unknown }).high24h;
      const low = (t as { low?: unknown; low24h?: unknown }).low ?? (t as { low24h?: unknown }).low24h;
      const vol = (t as { volume?: unknown; vol?: unknown }).volume ?? (t as { vol?: unknown }).vol;
      const idx = (t as { index?: unknown; indexPrice?: unknown }).index ?? (t as { indexPrice?: unknown }).indexPrice;
      const lastN = Number(String(last ?? "").replace(/,/g, ""));
      const lastDisp = last != null && last !== "" ? displayLast(last, pair) : "—";
      if (Number.isFinite(lastN) && lastN > 0) {
        const norm = normalizeSpotPrice(lastN, pair);
        if (norm != null && norm > 0) setPx(norm);
      }
      setTickerMeta({
        high: high != null ? displayLast(high, pair) : "—",
        low: low != null ? displayLast(low, pair) : "—",
        volume: vol != null ? String(vol).replace(/\.?0+$/, "") : "—",
        index: idx != null ? displayLast(idx, pair) : lastDisp,
      });
      if (token) {
        try {
          setOpens(await fetchSpotOpenOrders(token));
        } catch {
          setOpens([]);
        }
      } else {
        setOpens([]);
      }
    } catch (e) {
      const msg = errorMessage(e);
      if (msg !== "سشن" && msg !== "خطا") setWarn(msg);
      else setWarn("عمق محدود");
      setBook({ bids: [], asks: [] });
    }
  }, [token, pair, prices]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    fetchMarketCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
    fetchSpotFx()
      .then((f) => setFx(f.usdIrt ? String(f.usdIrt) : "—"))
      .catch(() => setFx("—"));
  }, []);

  useEffect(() => {
    void refreshPrices();
    const t = window.setInterval(() => void refreshPrices(), 12000);
    return () => clearInterval(t);
  }, [refreshPrices]);

  useEffect(() => {
    if (mode !== "spot") return;
    let cancelled = false;
    const load = async () => {
      try {
        const out = await fetchSpotOhlc(token || "", pair, { interval: "1m", limit: 120 });
        if (cancelled) return;
        const cs = out.candles || [];
        setOhlc(cs);
        setOhlcSource(String(out.source || ""));
        setOhlcEmpty(cs.length ? null : String(out.emptyReason || "empty"));
        if (cs.length >= 2) {
          const closes = cs
            .map((c) => Number(String(c.close).replace(/,/g, "")))
            .filter((n) => Number.isFinite(n) && n > 0);
          if (closes.length) {
            const hi = Math.max(...closes);
            const lo = Math.min(...closes);
            setTickerMeta((prev) => ({
              ...prev,
              high: displayLast(hi, pair),
              low: displayLast(lo, pair),
            }));
          }
        }
      } catch {
        if (!cancelled) {
          setOhlc([]);
          setOhlcSource("");
          setOhlcEmpty("fetch_fail");
        }
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 30000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [mode, pair, token]);

  useEffect(() => {
    if (mode !== "prices") return;
    /* prices already polled globally */
  }, [mode]);

  useEffect(() => {
    if (mode !== "spot") return;
    let cancelled = false;
    fetchSpotMeta()
      .then((m) => {
        if (cancelled) return;
        const live = m.engineLiveOrderTypes || m.supportedOrderTypes || ["market", "limit"];
        setSpotMeta(live);
        if (m.fx?.usdIrt) setFx(String(m.fx.usdIrt));
      })
      .catch(() => {
        if (!cancelled) setSpotMeta(["market", "limit"]);
      });
    fetchSpotMarkets(token || "")
      .then((d) => {
        if (cancelled) return;
        const rows = (d.spot || []).filter((x) => x.pair || x.symbol);
        if (rows.length) {
          setSpotRows(rows);
          return;
        }
        return fetchSpotMeta().then((m) => {
          if (cancelled) return;
          if (m.pairsHint?.length) {
            setSpotRows(m.pairsHint.map((p) => ({ pair: p, symbol: p })));
          }
        });
      })
      .catch(() => undefined);
    void refreshSpot();
    const t = window.setInterval(() => void refreshSpot(), 8000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, token, refreshSpot]);

  useEffect(() => {
    if (!token) {
      setAvailQty(0);
      setAvailText("—");
      return;
    }
    const base = baseOf(pair).toUpperCase();
    const quote = pair.includes("_") ? pair.slice(pair.indexOf("_") + 1).toUpperCase() : "USDT";
    let cancelled = false;
    (async () => {
      try {
        if (side === "buy") {
          if (quote === "IRT" || pair.toUpperCase() === "NUSD_IRT") {
            const ib = await fetchIrrBalance(token);
            if (cancelled) return;
            const q = Number(ib.balanceIrr) || 0;
            setAvailQty(q);
            setAvailLabel("موجودی ریال");
            setAvailText(fmtAmount(q, { integer: true }));
          } else {
            const dep = await fetchDepositMe(token);
            if (cancelled) return;
            const q = Number(String(dep.balanceUsdt ?? "0").replace(/,/g, "")) || 0;
            setAvailQty(q);
            setAvailLabel("موجودی USDT");
            setAvailText(`${fmtAmount(q)} USDT`);
          }
        } else {
          // sell base asset
          if (base === "USDT") {
            const dep = await fetchDepositMe(token);
            if (cancelled) return;
            const q = Number(String(dep.balanceUsdt ?? "0").replace(/,/g, "")) || 0;
            setAvailQty(q);
            setAvailLabel("موجودی USDT");
            setAvailText(`${fmtAmount(q)} USDT`);
            return;
          }
          const env = getNetworkEnv();
          const wl = await listWallets(token, env);
          const nmc = (wl.items || []).find((w) => String(w.chain).toLowerCase() === "nmc");
          if (!nmc) {
            if (!cancelled) {
              setAvailQty(0);
              setAvailLabel(`موجودی ${base}`);
              setAvailText("0");
            }
            return;
          }
          const bal = await fetchNmcBalances(token, nmc.address);
          if (cancelled) return;
          const tokens = bal.tokens || {};
          const hit = Object.entries(tokens).find(([sym]) => {
            const u = sym.toUpperCase();
            return u === base || u.replace(/^N/, "N") === base;
          });
          const q = hit ? Number(String(hit[1]?.balance ?? "0").replace(/,/g, "")) || 0 : 0;
          setAvailQty(q);
          setAvailLabel(`موجودی ${base}`);
          setAvailText(`${fmtAmount(q, { integer: isMetalBase(base) })} ${base}`);
        }
      } catch {
        if (!cancelled) {
          setAvailQty(0);
          setAvailText("—");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, pair, side]);

  // Keep last known display price for buy% → base conversion
  useEffect(() => {
    const lastN = Number(String(ticker).replace(/,/g, ""));
    if (Number.isFinite(lastN) && lastN > 0) setPx(lastN);
    else {
      const base = baseOf(pair).toUpperCase();
      const hit = Object.values(prices).find((p) => String(p.symbol || "").toUpperCase() === base);
      const n = Number(hit?.priceUsd);
      if (Number.isFinite(n) && n > 0) setPx(n);
    }
  }, [ticker, pair, prices]);

  useEffect(() => {
    if (pct <= 0) return;
    if (!(availQty > 0)) return;
    const base = baseOf(pair).toUpperCase();
    const quote = quoteOf(pair);
    const integer = isMetalBase(base);
    const pairU = pair.toUpperCase();

    if (side === "sell") {
      const next = integer
        ? Math.max(0, Math.floor((availQty * pct) / 100))
        : Math.max(0, Number(((availQty * pct) / 100).toFixed(6)));
      setAmountNag(String(next));
      return;
    }

    // buy: pct of quote balance → base amount
    const quoteSpend = (availQty * pct) / 100;
    let price = px;
    if (!(price > 0)) {
      const hit = Object.values(prices).find((p) => String(p.symbol || "").toUpperCase() === base);
      price = Number(hit?.priceUsd) || 0;
    }
    if (!(price > 0)) return;

    // NUSD_IRT synthetic ticker already returns IRT per NUSD — do NOT × fx again
    let pxQuote = price;
    if (quote === "IRT" && pairU !== "NUSD_IRT") {
      const fxN = Number(String(fx).replace(/,/g, ""));
      if (fxN > 0) pxQuote = price * fxN;
    }
    const baseAmt = quoteSpend / pxQuote;
    const next = integer
      ? Math.max(0, Math.floor(baseAmt))
      : Math.max(0, Number(baseAmt.toFixed(6)));
    setAmountNag(String(next));
  }, [pct, availQty, side, pair, px, prices, fx]);

  useEffect(() => {
    setPct(0);
  }, [pair, side]);

  useEffect(() => {
    if (!filteredPairs.length) return;
    if (!filteredPairs.includes(pair)) setPair(filteredPairs[0]);
  }, [filteredPairs, pair]);

  useEffect(() => {
    const hint = readPairHint();
    if (!hint || !filteredPairs.length) return;
    if (filteredPairs.includes(hint) && hint !== pair) setPair(hint);
    try {
      sessionStorage.removeItem(SPOT_PAIR_HINT_KEY);
    } catch {
      /* ignore */
    }
  }, [filteredPairs]);

  async function onPlace() {
    if (!token || requireAuthForTrade) {
      toast.err("سشن");
      onNeedAuth?.();
      return;
    }
    const amtRaw = Number(String(amountNag).replace(/,/g, ""));
    if (!Number.isFinite(amtRaw) || !(amtRaw > 0)) {
      toast.err("مقدار");
      return;
    }
    setBusy(true);
    try {
      const base = baseOf(pair);
      const tokenSymbol =
        activeRow?.tokenSymbol ||
        (base === "NAG" ? "nAg" : base === "NOCT" ? "NOCT" : base === "NUSD" ? "NUSD" : base);
      const amountNagN = isMetalBase(base)
        ? Math.max(1, Math.round(amtRaw))
        : Number(amtRaw.toFixed(8));
      if (!(amountNagN > 0)) {
        toast.err("مقدار");
        return;
      }
      await placeSpotOrder(token, {
        side,
        orderType,
        amountNag: amountNagN,
        limitUsdPerNag: orderType === "limit" || orderType.startsWith("target") ? limit || undefined : undefined,
        triggerPrice:
          orderType !== "market" && orderType !== "limit" ? trigger || undefined : undefined,
        tokenSymbol,
        pair,
      });
      toast.ok("ثبت");
      await refreshSpot();
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(id: string) {
    if (!token || !id) return;
    try {
      await cancelSpotOrder(token, id);
      toast.ok("لغو");
      await refreshSpot();
    } catch (e) {
      toast.err(errorMessage(e));
    }
  }

  const bids = levelsOf(book, "bids");
  const asks = levelsOf(book, "asks");
  const stopOrders = opens.filter((o) => {
    const t = String(o.type || o.orderType || "").toLowerCase();
    return t.includes("stop") || t.includes("target");
  });
  const openPlain = opens.filter((o) => !stopOrders.includes(o));
  const list = ordersTab === "stop" ? stopOrders : openPlain;

  return (
    <section className="panel wx-market" dir="rtl">
      <div className="wx-mode-tabs">
        <ChipTab active={mode === "spot"} onClick={() => setMode("spot")}>
          اسپات
        </ChipTab>
        <ChipTab active={mode === "prices"} onClick={() => setMode("prices")}>
          بازار
        </ChipTab>
      </div>
      {warn && !warn.includes("401") && !warn.includes("HTTP_") ? (
        <StatusChip tone="warn">{warn}</StatusChip>
      ) : null}

      {mode === "prices" && (
        <MarketList
          catalog={catalog}
          prices={prices}
          onTrade={(symbol, network) => {
            const hint = `${symbol.toUpperCase()}_USDT`;
            setSpotNet(network === "bsc" || network === "nmc" ? network : "all");
            setPair(hint);
            setMode("spot");
          }}
        />
      )}

      {mode === "spot" && (
        <div className="wx-spot">
          <div className="wx-mode-tabs">
            {(
              [
                ["all", "همه"],
                ["nmc", "nmc"],
                ["bsc", "bsc"],
              ] as const
            ).map(([n, label]) => (
              <ChipTab key={n} active={spotNet === n} onClick={() => setSpotNet(n)}>
                {label}
              </ChipTab>
            ))}
          </div>

          <SelectField
            label="جفت"
            value={pair}
            onChange={setPair}
            options={filteredPairs.map((p) => {
              const row = spotRows.find(
                (r) => String(r.pair || r.symbol || "").toUpperCase() === p
              );
              const net = row?.networkCode ? ` · ${row.networkCode}` : "";
              return { value: p, label: `${p}${net}` };
            })}
          />

          <div className="wx-pair-header">
            <div>
              <div className="wx-last ltr">{ticker}</div>
              <div className="muted">آخرین</div>
            </div>
            <div className="wx-fx">
              <div className="ltr">{fx}</div>
              <div className="muted">USD/IRT</div>
            </div>
          </div>

          <div className="wx-mode-tabs" style={{ marginBottom: 8 }}>
            {String(activeRow?.networkCode || "").toLowerCase() === "bsc" ? (
              <StatusChip tone="buy">BSC</StatusChip>
            ) : (
              <StatusChip tone="buy">NMC</StatusChip>
            )}
            {(book as { degraded?: boolean } | null)?.degraded ? (
              <StatusChip tone="warn">عمق محدود</StatusChip>
            ) : (
              <StatusChip tone="buy">زنده</StatusChip>
            )}
          </div>

          <PairStats
            last={ticker}
            high={tickerMeta.high || "—"}
            low={tickerMeta.low || "—"}
            volume={tickerMeta.volume || "—"}
            indexPrice={tickerMeta.index || "—"}
          />

          <ChartTabs
            tab={chartTab}
            onChange={setChartTab}
            last={ticker}
            candles={ohlc}
            source={ohlcSource}
            emptyReason={ohlcEmpty}
          />

          <div className="wx-spot-grid">
            <OrderBook asks={asks} bids={bids} last={ticker} pair={pair} />
            <TradeForm
              side={side}
              onSide={setSide}
              orderTypes={spotMeta}
              orderType={orderType}
              onOrderType={setOrderType}
              amount={amountNag}
              onAmount={(v) => {
                setPct(0);
                const base = baseOf(pair).toUpperCase();
                if (isMetalBase(base)) {
                  const d = v.replace(/[^\d]/g, "");
                  setAmountNag(d || "");
                  return;
                }
                setAmountNag(v);
              }}
              amountSuffix={baseOf(pair)}
              quoteSuffix={quoteOf(pair)}
              limit={limit}
              onLimit={setLimit}
              trigger={trigger}
              onTrigger={setTrigger}
              pct={pct}
              onPct={setPct}
              busy={busy}
              disabled={!token || requireAuthForTrade}
              onSubmit={() => void onPlace()}
              availableLabel={availLabel}
              availableText={availText}
            />
          </div>

          <div className="wx-orders">
            <div className="wx-mode-tabs">
              <ChipTab active={ordersTab === "open"} onClick={() => setOrdersTab("open")}>
                باز
              </ChipTab>
              <ChipTab active={ordersTab === "stop"} onClick={() => setOrdersTab("stop")}>
                توقف
              </ChipTab>
              <button type="button" className="wx-chip" onClick={() => void refreshSpot()}>
                ↻
              </button>
            </div>
            {!list.length && <p className="muted">۰</p>}
            {list.map((o, idx) => {
              const id = orderId(o);
              return (
                <article key={id || `o-${idx}`} className="wx-order-card">
                  <div className="row-between">
                    <StatusChip tone={o.side === "sell" ? "sell" : "buy"}>
                      {o.side === "sell" ? "فروش" : "خرید"}
                    </StatusChip>
                    <span className="muted ltr">{o.type || o.orderType}</span>
                  </div>
                  <div className="row-between">
                    <span className="ltr">{String(o.amountNag ?? "")}</span>
                    <span className="muted">{o.status}</span>
                  </div>
                  {id && token ? (
                    <button type="button" className="wx-cancel" onClick={() => void onCancel(id)}>
                      لغو
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
